import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './ProductListPage.module.scss';
import ProductCard from '../../components/common/ProductCard/ProductCard';
import productService from '../../services/productService';
import adminService from '../../services/adminService';
// [SEO] Import Helmet
import { Helmet } from 'react-helmet-async';
import { 
  IoSearchOutline, IoFilterOutline, IoArrowUpOutline, IoCloseCircle, IoGridOutline, IoLeafOutline
} from 'react-icons/io5';

// Skeleton Loading
const ProductSkeleton = () => (
  <div className={styles.skeletonCard}>
    <div className={`${styles.skeleton} ${styles.skeletonImg}`}></div>
    <div className={`${styles.skeleton} ${styles.skeletonText}`} style={{width: '70%'}}></div>
    <div className={`${styles.skeleton} ${styles.skeletonText}`} style={{width: '40%'}}></div>
  </div>
);

const ProductListPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Filter States
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category_id') || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // UX States
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  const itemsPerPage = 6;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [prodRes, catRes] = await Promise.all([
          productService.getProducts(selectedCategory),
          adminService.getCategories()
        ]);

        let productsData = [];
        if (prodRes.data && Array.isArray(prodRes.data.records)) {
            productsData = prodRes.data.records; 
        } else if (Array.isArray(prodRes.data)) {
            productsData = prodRes.data; 
        }

        setProducts(productsData);
        setCategories(Array.isArray(catRes.data) ? catRes.data : []);
      } catch (err) {
        console.error("Lỗi tải dữ liệu:", err);
        setProducts([]); 
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  useEffect(() => {
    const catId = searchParams.get('category_id');
    if (catId) setSelectedCategory(catId);
  }, [searchParams]);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];
    return products.filter(p => {
      return (p.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [products, searchTerm]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage, 
    currentPage * itemsPerPage
  );

  const handleCategoryClick = (catId) => {
    setSelectedCategory(catId);
    setCurrentPage(1);
    setShowMobileFilter(false); 
    setSearchParams(catId === 'all' ? {} : { category_id: catId });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearCategory = (e) => {
    e.stopPropagation();
    handleCategoryClick('all');
  };

  const currentCategoryName = useMemo(() => {
    if (selectedCategory === 'all') return 'Lọc Danh Mục';
    const cat = categories.find(c => String(c.id) === String(selectedCategory));
    return cat ? cat.name : 'Lọc Danh Mục';
  }, [selectedCategory, categories]);

  // --- [SEO LOGIC] ---
  const pageTitle = useMemo(() => {
    if (selectedCategory === 'all') return 'Danh Sách Sản Phẩm - Hương Trầm Cổ Mộc';
    const cat = categories.find(c => String(c.id) === String(selectedCategory));
    return cat ? `${cat.name} - Hương Trầm Cổ Mộc` : 'Sản Phẩm - Hương Trầm Cổ Mộc';
  }, [selectedCategory, categories]);

  const pageDescription = "Khám phá bộ sưu tập Trầm Hương tự nhiên, Trà Trầm và các sản phẩm thủ công mỹ nghệ cao cấp tại Hương Trầm Cổ Mộc.";
  const pageUrl = window.location.href;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": pageTitle,
    "description": pageDescription,
    "url": pageUrl,
    "numberOfItems": filteredProducts.length,
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": currentProducts.map((prod, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://huongtramcomoc.vn/san-pham/${prod.id}`,
        "name": prod.name
      }))
    }
  };
  // ------------------

  return (
    <div className={styles.pageContainer}>
      {/* --- HELMET SEO --- */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Hương Trầm Cổ Mộc" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>
      {/* ----------------- */}

      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <span className={styles.subHeader}>— Tinh Hoa Đất Trời —</span>
          <h1 className={styles.mainTitle}>
            {selectedCategory === 'all' 
              ? 'TẤT CẢ SẢN PHẨM' 
              : categories.find(c => String(c.id) === String(selectedCategory))?.name || 'Sản Phẩm'}
          </h1>
          <p className={styles.descText}>
            Hương thơm thuần khiết, lưu giữ giá trị thời gian.
          </p>
        </div>
      </div>

      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          
          {/* Mobile Controls: Search & Filter */}
          <div className={styles.mobileControls}>
            <div className={styles.searchWrapper}>
              <div className={styles.searchBox}>
                <input 
                  type="text" 
                  placeholder="Tìm phẩm vật..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button><IoSearchOutline /></button>
              </div>
            </div>

            <div className={styles.filterBtnWrapper}>
              <button 
                className={`${styles.mobileCategoryToggle} ${selectedCategory !== 'all' ? styles.hasFilter : ''}`}
                onClick={() => setShowMobileFilter(!showMobileFilter)}
              >
                {/* Text đã được style trong CSS để không bị tràn */}
                <span className={styles.toggleText}>
                  {selectedCategory !== 'all' ? currentCategoryName : 'Lọc'}
                </span>
                {selectedCategory !== 'all' ? (
                  <span className={styles.clearIcon} onClick={clearCategory}><IoCloseCircle /></span>
                ) : (
                  <span className={styles.toggleIcon}><IoFilterOutline /></span>
                )}
              </button>
            </div>
          </div>

          <div className={`${styles.categoryList} ${showMobileFilter ? styles.open : ''}`}>
            <h3 className={styles.catTitle}>
              Danh Mục
            </h3>
            <ul>
              <li 
                className={selectedCategory === 'all' ? styles.active : ''} 
                onClick={() => handleCategoryClick('all')}
              >
                <span className={styles.catName}>Tất cả sản phẩm</span>
              </li>
              {categories.map(cat => (
                <li 
                  key={cat.id} 
                  className={String(selectedCategory) === String(cat.id) ? styles.active : ''}
                  onClick={() => handleCategoryClick(cat.id)}
                >
                  <span className={styles.catName}>{cat.name}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <main className={styles.mainContent}>
          <div className={styles.infoBar}>
            <span className={styles.resultText}>
              Tìm thấy <b>{filteredProducts.length}</b> tuyệt phẩm
            </span>
            <div className={styles.decorativeLine}></div>
          </div>

          <div className={styles.productGrid}>
            {loading ? (
              [...Array(6)].map((_, i) => <ProductSkeleton key={i} />)
            ) : currentProducts.length > 0 ? (
              currentProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))
            ) : (
              <div className={styles.noResult}>
                <div className={styles.noResultIcon}><IoLeafOutline /></div>
                <p>Chưa tìm thấy phẩm vật phù hợp với quý khách.</p>
                <button onClick={() => {setSearchTerm(''); handleCategoryClick('all');}} className={styles.resetBtn}>
                  Xem tất cả
                </button>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button 
                className={styles.pageButton} 
                disabled={currentPage === 1}
                onClick={() => {setCurrentPage(p => p - 1); window.scrollTo(0,0);}}
              >
                ←
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i} 
                  className={`${styles.pageButton} ${currentPage === i + 1 ? styles.activePage : ''}`}
                  onClick={() => {setCurrentPage(i + 1); window.scrollTo(0,0);}}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className={styles.pageButton} 
                disabled={currentPage === totalPages}
                onClick={() => {setCurrentPage(p => p + 1); window.scrollTo(0,0);}}
              >
                →
              </button>
            </div>
          )}
        </main>
      </div>

      <button 
        className={`${styles.backToTop} ${showScrollTop ? styles.visible : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <IoArrowUpOutline />
      </button>
    </div>
  );
};

export default ProductListPage;